import { X, Download, Mail, Mails, BellRing } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { ChartBox, MonthTip, inr, num, ageOf, ageChip, PAYMENT_STATUS_LIST, PAYMENT_COLORS } from "./shared";

const SummaryBanner = ({ invoices }) => {
  const collectedOf = (x) => (x.payment_status === "Paid" ? num(x.total) : Math.min(num(x.amount_received), num(x.total)));
  const paidSum = invoices.reduce((s, x) => s + collectedOf(x), 0);
  const openSum = invoices.reduce((s, x) => s + Math.max(0, num(x.total) - collectedOf(x)), 0);
  const settledCount = invoices.filter((x) => x.payment_status === "Paid").length;
  return (
    <div className="grid grid-cols-3 gap-3 mb-6" data-testid="inv-summary-banner">
      <div className="border border-white/10 bg-white/[0.02] p-4" data-testid="inv-summary-total">
        <p className="font-plexmono text-[9px] uppercase tracking-[0.2em] text-neutral-500">Total Invoiced</p>
        <p className="text-xl text-white mt-2">{inr(paidSum + openSum)}</p>
        <p className="font-plexmono text-[9px] text-neutral-600 mt-1 uppercase tracking-[0.1em]">{invoices.length} invoice{invoices.length === 1 ? "" : "s"} · incl. GST</p>
      </div>
      <div className="border border-[#5C8A6E]/40 bg-[#5C8A6E]/5 p-4" data-testid="inv-summary-paid">
        <p className="font-plexmono text-[9px] uppercase tracking-[0.2em] text-neutral-500">Collected</p>
        <p className="text-xl text-[#5C8A6E] mt-2">{inr(paidSum)}</p>
        <p className="font-plexmono text-[9px] text-neutral-600 mt-1 uppercase tracking-[0.1em]">{settledCount} paid · incl. part payments</p>
      </div>
      <div className="border border-[#D9A441]/40 bg-[#D9A441]/5 p-4" data-testid="inv-summary-outstanding">
        <p className="font-plexmono text-[9px] uppercase tracking-[0.2em] text-neutral-500">Outstanding</p>
        <p className="text-xl text-[#D9A441] mt-2">{inr(openSum)}</p>
        <p className="font-plexmono text-[9px] text-neutral-600 mt-1 uppercase tracking-[0.1em]">{invoices.length - settledCount} to collect</p>
      </div>
    </div>
  );
};

const MonthlyChart = ({ invoices }) => {
  const months = {};
  invoices.forEach((inv) => {
    if (!inv.invoice_date) return;
    const dt = new Date(inv.invoice_date);
    const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
    months[key] = months[key] || { key, label: dt.toLocaleDateString("en-IN", { month: "short", year: "2-digit" }), invoiced: 0, collected: 0 };
    months[key].invoiced += num(inv.total);
    if (inv.payment_status === "Paid") months[key].collected += num(inv.total);
  });
  const data = Object.values(months).sort((a, b) => a.key.localeCompare(b.key));
  if (!data.length) return null;
  return (
    <div className="border border-white/10 bg-white/[0.02] p-4 mb-6" data-testid="inv-chart">
      <div className="flex items-center justify-between mb-3">
        <p className="font-plexmono text-[9px] uppercase tracking-[0.25em] text-neutral-500">Monthly Collections</p>
        <div className="flex gap-4 font-plexmono text-[9px] uppercase tracking-[0.15em] text-neutral-500">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2" style={{ background: "#D9A441" }} /> Invoiced</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2" style={{ background: "#5C8A6E" }} /> Collected</span>
        </div>
      </div>
      <ChartBox className="h-44">
        {(cw, ch) => (
          <BarChart width={cw} height={ch} data={data} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fill: "#737373", fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={(v) => (v >= 1e7 ? `${(v / 1e7).toFixed(0)}Cr` : v >= 1e5 ? `${(v / 1e5).toFixed(0)}L` : v)} tick={{ fill: "#737373", fontSize: 10 }} axisLine={false} tickLine={false} width={42} />
            <Tooltip content={<MonthTip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
            <Bar dataKey="invoiced" name="Invoiced" fill="#D9A441" />
            <Bar dataKey="collected" name="Collected" fill="#5C8A6E" />
          </BarChart>
        )}
      </ChartBox>
    </div>
  );
};

const InvoiceRow = ({ inv, reminderSending, emailSending, onPaymentStatus, onAmountReceived, onDownload, onRemind, onEmail }) => (
  <div className="border border-white/10 bg-white/[0.02] p-4" data-testid={`inv-row-${inv.invoice_no}`}>
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <p className="font-plexmono text-xs text-[#D9A441]" data-testid={`inv-no-${inv.id}`}>{inv.invoice_no}</p>
        <p className="text-sm text-white mt-1.5">{inv.client}</p>
        <p className="font-plexmono text-[10px] text-neutral-500 mt-0.5 uppercase tracking-[0.1em]">Bill To: {inv.billed_to || "—"} · {inv.product}</p>
        <p className="font-plexmono text-[10px] text-neutral-600 mt-0.5 uppercase tracking-[0.1em]" data-testid={`inv-date-${inv.id}`}>
          Issued {inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
          {ageChip(inv)}
        </p>
        {inv.reminder_log?.length > 0 && (
          <p className="font-plexmono text-[9px] text-neutral-600 mt-0.5 uppercase tracking-[0.1em]" data-testid={`inv-remind-log-${inv.id}`} title={inv.reminder_log.map((r) => `${new Date(r.at).toLocaleString("en-IN")} → ${r.to}`).join("\n")}>
            Reminded {inv.reminder_log.length}× · last {new Date(inv.reminder_log[inv.reminder_log.length - 1].at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
          </p>
        )}
        {inv.email_issue && (
          <p className="font-plexmono text-[9px] text-[#E6965C] mt-0.5 uppercase tracking-[0.1em]" data-testid={`inv-email-issue-${inv.id}`}>Bill To email undeliverable — re-check in Edit Deal</p>
        )}
      </div>
      <div className="text-right">
        <p className="font-plexmono text-[9px] uppercase tracking-[0.15em] text-neutral-500">Fee {inr(inv.fee)}</p>
        <p className="text-lg text-white mt-0.5" data-testid={`inv-total-${inv.id}`}>{inr(inv.total)}</p>
        <p className="font-plexmono text-[9px] text-neutral-600 uppercase tracking-[0.1em]">incl. GST @ 18%</p>
        {inv.payment_status === "Part-Paid" && (
          <p className="font-plexmono text-[9px] text-[#D9A441] uppercase tracking-[0.1em] mt-0.5" data-testid={`inv-balance-${inv.id}`}>
            Received {inr(Math.min(num(inv.amount_received), num(inv.total)))} · Balance {inr(Math.max(0, num(inv.total) - num(inv.amount_received)))}
          </p>
        )}
      </div>
    </div>
    <div className="flex items-center justify-between gap-3 mt-4 pt-3 border-t border-white/5 flex-wrap">
      <select value={inv.payment_status} onChange={(e) => onPaymentStatus(inv, e.target.value)} data-testid={`inv-status-${inv.id}`} className={`bg-transparent border px-3 py-2 font-plexmono text-[10px] uppercase tracking-[0.15em] outline-none cursor-pointer ${PAYMENT_COLORS[inv.payment_status] || PAYMENT_COLORS.Unpaid}`}>
        {PAYMENT_STATUS_LIST.map((s) => <option key={s} value={s} className="bg-[#111112] text-neutral-200">{s}</option>)}
      </select>
      {inv.payment_status === "Part-Paid" && (
        <label className="flex items-center gap-2" data-testid={`inv-received-wrap-${inv.id}`}>
          <span className="font-plexmono text-[9px] uppercase tracking-[0.15em] text-neutral-500">Received ₹</span>
          <input type="number" min="0" step="any" defaultValue={inv.amount_received || ""} key={`${inv.id}-${inv.amount_received}`}
            onBlur={(e) => Number(e.target.value || 0) !== num(inv.amount_received) && onAmountReceived(inv, e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
            data-testid={`inv-received-${inv.id}`}
            className="w-32 bg-white/[0.03] border border-white/10 px-2.5 py-2 text-sm text-neutral-100 outline-none focus:border-[#D9A441] transition-colors rounded-none font-plexmono" />
        </label>
      )}
      <div className="flex items-center gap-2">
        {inv.kind === "deal" && inv.payment_status !== "Paid" && ageOf(inv.invoice_date) > 30 && (
          <button onClick={() => onRemind(inv)} disabled={reminderSending || !inv.recipient_email} data-testid={`inv-remind-${inv.id}`} title={inv.recipient_email ? `Send payment reminder to ${inv.recipient_email}` : "No recipient email on deal"} className="border border-[#F04864]/40 text-[#F04864] hover:bg-[#F04864] hover:text-white px-3 py-2 font-plexmono text-[9px] uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
            <BellRing size={12} /> {reminderSending ? "…" : "Remind"}
          </button>
        )}
        <button onClick={() => onDownload(inv)} data-testid={`inv-download-${inv.id}`} title="Download PDF" className="border border-white/15 hover:border-[#D9A441] text-neutral-300 p-2.5 transition-colors"><Download size={13} /></button>
        {inv.kind === "deal" && (
          <button onClick={() => onEmail(inv)} disabled={emailSending || !inv.recipient_email} data-testid={`inv-email-${inv.id}`} title={inv.recipient_email ? `Email PDF to ${inv.recipient_email}` : "No recipient email on deal"} className="border border-white/15 hover:border-[#D9A441] text-neutral-300 p-2.5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"><Mail size={13} /></button>
        )}
      </div>
    </div>
  </div>
);

export const InvoiceRegister = ({ invoices, reminderSending, digestSending, emailSending,
  onClose, onExport, onDigest, onPaymentStatus, onAmountReceived, onDownload, onRemind, onEmail }) => (
  <div className="fixed inset-0 z-50" data-testid="invoices-overlay">
    <div className="absolute inset-0 bg-black/70" onClick={onClose} />
    <aside className="absolute inset-y-0 right-0 w-full max-w-3xl bg-[#111112] border-l border-white/10 p-8 overflow-y-auto font-plex" data-testid="invoices-drawer">
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="font-plexmono text-[10px] uppercase tracking-[0.3em] text-[#D9A441]">Invoice Register</p>
          <h2 className="text-2xl font-medium text-white mt-2">Issued Invoices</h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onDigest} disabled={digestSending} data-testid="invoices-digest-btn" title="Email the overdue-invoice digest now (also auto-sent weekly)" className="border border-white/15 hover:border-[#D9A441] text-neutral-300 px-4 py-2.5 font-plexmono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 transition-colors disabled:opacity-40">
            <Mails size={13} /> {digestSending ? "Sending…" : "Digest"}
          </button>
          <button onClick={onExport} data-testid="invoices-export" className="border border-white/15 hover:border-[#D9A441] text-neutral-300 px-4 py-2.5 font-plexmono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 transition-colors">
            <Download size={13} /> Excel
          </button>
          <button onClick={onClose} data-testid="invoices-close" className="text-neutral-500 hover:text-white transition-colors"><X size={18} /></button>
        </div>
      </div>
      {invoices.length > 0 && <SummaryBanner invoices={invoices} />}
      <MonthlyChart invoices={invoices} />
      {invoices.length === 0 && (
        <p className="text-sm text-neutral-500" data-testid="invoices-empty">No invoices issued yet. Generate an invoice from a Closed Won deal to see it here.</p>
      )}
      <div className="space-y-3">
        {invoices.map((inv) => (
          <InvoiceRow key={`${inv.kind}-${inv.id}`} inv={inv}
            reminderSending={reminderSending} emailSending={emailSending}
            onPaymentStatus={onPaymentStatus} onAmountReceived={onAmountReceived}
            onDownload={onDownload} onRemind={onRemind} onEmail={onEmail} />
        ))}
      </div>
    </aside>
  </div>
);
